import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { getOperationType, rowStyle } from '../../components/common-constants';
import { hasWritePermission } from '../../../components/Authorization/hasWritePermission';
import { Button, Col, Divider, Empty, Form, Input, Typography, Row, Select, Space, Tooltip, Collapse } from 'antd';
import { getFormItems } from '../../components/form/formComponents';
import { fetchData, saveData } from '../../service/dataServices';
import moment from 'moment';
import CustomAddNewPageContent from '../../../components/Content/CustomAddNewPageContent';
import MainImageUploader from '../../components/image-upload/MainImageUploader';
import { EditOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router';
import Authorized from '../../../components/Authorization/Authorized';
import ActivityPage from '../../../pages/dashboard/Activity/ActivityPage';
import UserDropdown from '../../components/form/userDropdown';
import { localizeField } from '../../utils/localization';
import useDropdownFormItemHook from '../../components/form/DropdownFormItem';

const { Option } = Select;
const { Text } = Typography;

const SaveMovement = () => {
  const PATH = '/api/movements';
  const { id } = useParams();
  const operation = getOperationType(id);
  const hasWritePerm = hasWritePermission('movement');

  const history = useHistory();
  const [form] = Form.useForm();
  const [mainImageFileList, setMainImageFileList] = useState([]);
  const [initialData, setInitialData] = useState();
  const [dropdowns, setDropdowns] = useState();
  const [objects, setObjects] = useState([]);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [selectedObject, setSelectedObject] = useState({});
  const [refetchChangeHistory, setRefetchChangeHistory] = useState(null);
  const [objectFilters, setObjectFilters] = useState({
    page: 0,
    size: 20,
  });

  const {
    getDropdownFormItem,
    getInputFormItem,
    getDatePickerFormItem,
    getFormSubmitButton,
  } = getFormItems(dropdowns, hasWritePerm);

  fetchData('/api/dropdowns', setDropdowns);
  if (id) {
    fetchData(PATH + '/' + id, setInitialData);
  }
  const objectResponse = fetchData('/api/objects', data => setObjects(data.content), objectFilters);
  const saveMutation = saveData(PATH, operation.method);
  const fetchObjects = fetchData('/api/objects/' + selectedObjectId, setSelectedObject, null, false);
  const fetchNextReportId = fetchData(PATH + '/reportId', (p) => ({}), null, false);
  const { singleSelectFormItem } = useDropdownFormItemHook(hasWritePerm)

  useEffect(() => {
    if (selectedObjectId) {
      fetchObjects.refetch().then(resp => {
        let data = resp.data.data;
        const found = objects.find(o => o.id === data.id);
        if (!found) {
          setObjects(prev => [...prev, { id: data.id, inventoryNumber: data.inventoryNumber }]);
        }
      });
    }
  }, [selectedObjectId]);

  useEffect(() => {
    if (selectedObject) {
      if (selectedObject.mainImage) {
        setMainImageFileList([selectedObject.mainImage]);
      } else {
        setMainImageFileList([]);
      }
    }
  }, [selectedObject]);

  useEffect(() => {
    if (initialData && dropdowns) {
      setSelectedObjectId(initialData.objectId);

      form.setFieldsValue({
        ...initialData,
        dateOfMovement: initialData.dateOfMovement ? moment(initialData.dateOfMovement) : null,
        dateOfReturn: initialData.dateOfReturn ? moment(initialData.dateOfReturn) : null,
      });
    }
  }, [initialData, dropdowns]);

  const triggerChangeHistoryRefetch = () => {
    setRefetchChangeHistory(new Date());
  }

  const onSearch = val => {
    if (val.length >= 4) {
      setObjectFilters((prev) => {
        return ({ ...prev, inventoryNumber: val });
      });
    }
  };

  const onClearSelectedObject = () => {
    setSelectedObjectId(null);
    setSelectedObject({});
    setMainImageFileList([]);
  };

  const onFinish = (values) => {
    const dataToSend = {
      ...values,
      objectId: selectedObjectId,
    };
    if (id) {
      saveMutation.mutate({ dataToSend, id }, { onSuccess: triggerChangeHistoryRefetch });
    } else {
      saveMutation.mutate({ dataToSend }, { onSuccess: triggerChangeHistoryRefetch });
    }
  };

  const viewObjectButton = selectedObjectId &&
    (<Authorized asset="item" operation="read">
      <Button icon={<ArrowRightOutlined />} onClick={() => history.push(`/save-object/${selectedObjectId}`)}>
        View Object
      </Button>
    </Authorized>
    )

  return (
    <CustomAddNewPageContent title={operation.type + ' Movement'} subTitle="Movements" exportButton={viewObjectButton}>
      <Row style={{ marginLeft: 20, marginRight: 20, justifyContent: 'space-between' }}>
        <Space>
          <Select
            showSearch
            placeholder="Find object by inventory number"
            allowClear
            value={selectedObjectId}
            onClear={onClearSelectedObject}
            onSelect={value => setSelectedObjectId(value)}
            onSearch={onSearch}
            filterOption={false}
            loading={objectResponse.isFetching}
            style={{ width: '36vh' }}
          >
            {
              objects.map(option => (
                <Option value={option.id} key={option.id}>{option.inventoryNumber}</Option>
              ))
            }
          </Select>
          {selectedObject && <Text copyable={{ text: selectedObject?.inventoryNumber }}></Text>}
        </Space>
      </Row>
      <Divider />

      <Form
        form={form}
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 16 }}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row style={rowStyle}>
          <Col span={8} style={{ display: 'flex', alignItems: 'center' }}>
            {mainImageFileList && mainImageFileList.length > 0 &&
              (<MainImageUploader
                disabledAdd={true}
                storageFolder="movement"
                fileList={mainImageFileList}
                setFileList={setMainImageFileList}
              />)}
            {mainImageFileList && mainImageFileList.length === 0 &&
              (<div>
                <Space direction="vertical">
                  <Empty description={false} />
                </Space>
              </div>)}
          </Col>
          <Col span={8}>
            <Space direction="vertical" size={14}>
              Inventory Number:
              <Input disabled={true} value={selectedObject.inventoryNumber} allowClear />

              Object Name:
              <Input disabled={true} value={selectedObject.objectName} allowClear />

              Object Title:
              <Input disabled={true} value={localizeField(selectedObject.objectTitle)} allowClear />
            </Space>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Report Id"
              name="reportId"
              rules={[{ required: true, message: `Report Id is required!` }]}
            >
              <Input
                disabled={!hasWritePermission}
                placeholder="Report Id"
                allowClear
                prefix={(
                  <Tooltip title="Fill automatically">
                    <Button
                      style={{ padding: 0 }}
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => {
                        fetchNextReportId.refetch().then(resp => {
                          if (resp.data && resp.data.data)
                            form.setFieldsValue({ reportId: resp.data.data });
                        });
                      }}
                    />
                  </Tooltip>)}
              />
            </Form.Item>

            {getDatePickerFormItem('dateOfMovement', 'Date Of Movement')}
            {getDatePickerFormItem('dateOfReturn', 'Date Of Return')}
          </Col>
        </Row>

        <Divider />

        <Row style={rowStyle}>
          <Col span={12}>
            {singleSelectFormItem('movementType', 'Movement Type')}
          </Col>
          <Col span={12}>
            {getInputFormItem('movedBy', 'Moved By (To Be Deleted)')}
          </Col>
          <Col span={12}>
            <UserDropdown label='Moved By' fieldName='movedByUserId' hasWritePermission={hasWritePerm} />
          </Col>
          <Col span={12}>
            {singleSelectFormItem('movementLocation', 'Old Location')}
          </Col>
          <Col span={12}>
            {getInputFormItem('supervisedBy', 'Supervised By (To Be Deleted)')}
          </Col>
          <Col span={12}>
            <UserDropdown label='Supervised By' fieldName='supervisedByUserId' hasWritePermission={hasWritePerm} />
          </Col>
          <Col span={12}>
            {getInputFormItem('newLocation', 'New Location')}
          </Col>
          <Col span={12}>
            {singleSelectFormItem('reasonForMovement', 'Reason For Movement')}
          </Col>
          <Col span={12}>
            {getInputFormItem('signature', 'Approval and Signature')}
          </Col>
        </Row>

        <Divider />
        {getFormSubmitButton(!selectedObject.id)}

      </Form>

      <Divider />
      {id && <Collapse
        bordered={false}
        items={[
          {
            key: 'change-list',
            label: 'Change History',
            forceRender: true,
            children: (
              <ActivityPage
                tableName='movement'
                entityId={id}
                fetchDataPath='/api/audit/logs/entity'
                refetchTrigger={refetchChangeHistory}
              />
            ),
          }]}
      />}
    </CustomAddNewPageContent>
  );
};

export default SaveMovement;
